import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertTriangle, ClipboardList } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { serpAnalysisApi } from "@/api/serp-analysis";

interface QuestionsTabProps {
  requestId: string;
}

export default function QuestionsTab({ requestId }: QuestionsTabProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["paa", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getPaa(requestId);
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center text-red-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>{(error as Error).message}</p>
      </div>
    );
  }

  if (!data?.data?.length) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">📋 People Also Ask 資料補充中</p>
          <p className="text-sm mt-1">
            此關鍵字的 PAA 資料將在重新觸發 SERP 後自動填入。
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground mb-3">
        共 {data.data.length} 個 People Also Ask 問題
      </p>
      <ol className="space-y-2">
        {data.data.map((item, i) => {
          // Extract question text from title or data_json
          const question =
            item.title ||
            (item.data_json as Record<string, unknown> | null)?.question as string ||
            `Question ${i + 1}`;
          return (
            <li key={i} className="flex gap-3 items-start p-3 rounded-md border bg-card">
              <span className="text-muted-foreground font-mono text-sm min-w-[1.5rem]">
                {i + 1}.
              </span>
              <span className="text-sm">{question}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
